import { useEffect, useState } from 'react';

/**
 * Returns a version of the value that only updates after the specified delay has passed
 * without the value changing.
 * * Common Use Case: Preventing expensive operations (like API search calls)
 * from firing on every single keystroke.
 *
 * @param value - The fast-changing source value (e.g., state from a text input).
 * @param delay - Time in ms to wait for inactivity before updating (default 500ms).
 * @returns The debounced value.
 */
export function useDebouncedValue<T>(value: T, delay = 500): T {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}
